/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

/**
 * Events Contract
 *
 * This contract defines the active event-related API endpoints for type-safe
 * client-server communication using oRPC. All routes are mounted under the
 * org-scoped controller prefix `/api/orgs/{orgId}/events` at runtime; the paths
 * in this contract keep the legacy `/api/events/...` shape for oRPC typing.
 *
 * Endpoints:
 * - GET    /api/events                    - List events with filtering and pagination
 * - GET    /api/events/id/{id}            - Get event by numeric ID
 * - GET    /api/events/{phoneE164}        - Get events by phone number
 * - POST   /api/events                    - Create a new event
 * - PATCH  /api/events/{id}               - Modify an existing event
 * - POST   /api/events/{id}/cancel        - Cancel an event by ID
 * - POST   /api/events/{id}/reinstate     - Reinstate a cancelled event
 * - POST   /api/events/{id}/complete      - Mark an event completed
 * - POST   /api/events/{id}/no-show       - Mark an event no-show
 * - DELETE /api/events/user/{phoneE164}   - Cancel user events by phone
 * - GET    /api/events/upcoming/{phoneE164} - Find upcoming event for user
 * - POST   /api/events/available-slots    - Get available time slots for dates
 */

import { oc } from "@orpc/contract";
import * as z from "zod";
import {
	EventStatusSchema,
	type NotificationSourceSchema,
} from "../common/enums";
import {
	commonErrors,
	PhoneE164Schema,
	StrictPhoneE164InputSchema,
} from "./base";
import { EventBase } from "./prisma-base";

export { EventStatusSchema, NotificationSourceSchema } from "../common/enums";

// =============================================================================
// Schemas
// =============================================================================

/**
 * Event type - accepts any non-empty string to support dynamic, config-driven event types.
 */
export const EventTypeSchema = z.string().min(1).max(100);

/**
 * Event list item schema - matches backend response structure.
 *
 * The handler (`events.procedures.ts` getByPhone/list) emits the Prisma
 * subset below PLUS presentation-only fields (user_name, phone_e164,
 * cancelled boolean, has_document). Derivation makes the Prisma subset
 * drift-proof; the extension covers the API-specific shape.
 */
export const EventListItemSchema = EventBase.pick({
	id: true,
	eventDateTime: true,
}).extend({
	/**
	 * Exclusive end of the booking's interval: `eventDateTime + durationMinutes`
	 * for a fixed-duration type, the guest's checkout for a multi-day one. The
	 * calendar draws a spanning bar whenever it crosses a day boundary. Server
	 * responses always provide it (the column is NOT NULL); optionality, as with
	 * `contact_id` below, permits client-only optimistic rows.
	 */
	endsAt: z.string().datetime().optional(),
	user_id: z.string(),
	/**
	 * The contact whose document and profile belong to this event. List responses
	 * always provide it; optionality also permits client-only optimistic rows.
	 */
	contact_id: z.string().min(1).optional(),
	phone_e164: PhoneE164Schema.nullish(),
	user_name: z.string().nullish(),
	type: z.string().min(1),
	status: EventStatusSchema,
	cancelled: z.boolean(),
	has_document: z.boolean(),
	/** True when the event is for a phoneless beneficiary booked under a contactor. */
	is_beneficiary: z.boolean().nullish(),
	/** The managing contactor's display name; null/absent unless a beneficiary. */
	contactor_name: z.string().nullish(),
	/** Per-event dynamic custom-field values (keyed by field-definition name). */
	customFieldValues: z.record(z.string(), z.unknown()).nullish(),
});

/**
 * Full event schema - for detailed event responses (`getById`).
 *
 * Derived from the Prisma `Event` model. Prisma-only columns that the
 * handler does not serialize today (version, contactId, metadata, orgId,
 * uid, eventTypeId, endsAt, customFieldValues) are intentionally omitted
 * from the wire shape. The drift guard's allowlist at
 * `server/scripts/validate-zod-drift.ts` records that decision so future
 * schema changes can't silently expand or regress the surface.
 */
export const EventSchema = EventBase.pick({
	id: true,
	eventDateTime: true,
	type: true,
	userId: true,
	cancelledAt: true,
	createdAt: true,
	updatedAt: true,
}).extend({
	status: EventStatusSchema,
});

/**
 * Create event response schema.
 */
export const CreateEventResponseSchema = z.object({
	eventId: z.string(),
	contactId: z.string().trim().min(1),
	status: EventStatusSchema.optional(),
	phoneE164: PhoneE164Schema.optional(),
	eventDateTime: z.string().datetime(),
});

/**
 * Modify event response schema.
 */
export const ModifyEventResponseSchema = z.object({
	eventId: z.string(),
});

/**
 * Structured key-value note fields for an event.
 */
export const EventNoteFieldsSchema = z.record(
	z.string().min(1).max(200),
	z.string().max(2000)
);

/**
 * tldraw canvas snapshot payload (document/session JSON) for event notes.
 */
export const EventNoteCanvasSchema = z.record(z.string(), z.unknown());

/**
 * Event notes response schema (`GET/PATCH /{id}/notes`).
 */
export const EventNotesResponseSchema = z.object({
	eventId: z.string(),
	notes: z.string().nullable(),
	noteFields: EventNoteFieldsSchema.nullable(),
	noteCanvas: EventNoteCanvasSchema.nullable(),
	customFieldValues: z.record(z.string(), z.unknown()).nullable(),
});

/**
 * Update the notes attached to an event. `undefined` leaves a section
 * untouched; `null` clears it.
 */
export const UpdateEventNotesInputSchema = z.object({
	notes: z.string().max(20_000).nullish(),
	noteFields: EventNoteFieldsSchema.nullish(),
	noteCanvas: EventNoteCanvasSchema.nullish(),
	customFieldValues: z.record(z.string(), z.unknown()).nullish(),
});

/**
 * Event history (audit timeline) schemas — mirror of the server's
 * `domain/schemas/event/event-history.schema.ts`.
 */
export const EventHistoryActionSchema = z.enum([
	"created",
	"rescheduled",
	"updated",
	"cancelled",
	"reinstated",
	"completed",
	"no_show",
]);

export const EventHistoryChangeSchema = z.object({
	field: z.enum(["eventDateTime", "type", "userName"]),
	from: z.string().nullable(),
	to: z.string().nullable(),
});

export const EventHistoryActorSchema = z.object({
	userId: z.string().nullable(),
	name: z.string().nullable(),
	source: z.string().nullable(),
});

export const EventHistoryEntrySchema = z.object({
	id: z.string(),
	action: EventHistoryActionSchema,
	occurredAt: z.string(),
	eventDateTime: z.string().nullable(),
	actor: EventHistoryActorSchema,
	changes: z.array(EventHistoryChangeSchema),
});

/**
 * The booking's subject + beneficiary→contactor relationship. Present when the
 * event's contact is a phoneless relative booked on behalf of a contactor, so
 * the timeline can show "Booked under {contactor}". Mirror of the server's
 * `EventHistorySubjectSchema`.
 */
export const EventHistorySubjectSchema = z.object({
	contactId: z.string(),
	name: z.string().nullable(),
	isBeneficiary: z.boolean(),
	contactorContactId: z.string().nullable(),
	contactorName: z.string().nullable(),
});
export type EventHistorySubject = z.infer<typeof EventHistorySubjectSchema>;

/**
 * Event history response schema (`GET /{id}/history`). Entries are ordered
 * oldest → newest.
 */
export const EventHistoryResponseSchema = z.object({
	entries: z.array(EventHistoryEntrySchema),
	subject: EventHistorySubjectSchema.nullable(),
});

/**
 * Cancel/Reinstate event response schema.
 */
export const CancelEventResponseSchema = z.object({
	cancelled: z.boolean(),
});

/**
 * Complete event response schema (`POST /{id}/complete`).
 */
export const CompleteEventResponseSchema = z.object({
	completed: z.boolean(),
});

/**
 * No-show event response schema (`POST /{id}/no-show`).
 */
export const MarkNoShowResponseSchema = z.object({
	markedNoShow: z.boolean(),
});

/**
 * Origin sources a dashboard/HTTP caller may legitimately stamp on a create.
 * The AI/system sources (`agent`, `system_agent`) are reserved for
 * non-interactive callers and rejected here.
 */
export const DashboardEventSourceSchema = z.enum(["local", "external_user"]);

/**
 * Cancel by phone response schema (returns count).
 */
export const CancelByPhoneResponseSchema = z.object({
	cancelled: z.number().int().min(0),
});

/**
 * Find upcoming event response schema.
 */
export const FindUpcomingEventResponseSchema = z.object({
	eventId: z.string().nullable(),
});

/**
 * Available time slot for a single date.
 */
export const AvailableTimeSlotsDateSchema = z.object({
	date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
	time_slots: z.array(z.string()),
});

/**
 * Get available time slots batch response schema.
 */
export const GetAvailableSlotsBatchResponseSchema = z.object({
	dates: z.array(AvailableTimeSlotsDateSchema),
	// Booking-policy note (e.g. dates omitted because they are beyond the
	// caller role's per-event-type booking horizon).
	note: z.string().optional(),
});

/**
 * List events response schema.
 */
export const ListEventsResponseSchema = z.object({
	events: z.array(EventListItemSchema),
	total_count: z.number().int().min(0),
	hasMore: z.boolean(),
	nextCursor: z.string().nullish(),
});

// =============================================================================
// Contract Definition
// =============================================================================

export const eventsContract = {
	/**
	 * List events with optional filtering and pagination.
	 *
	 * Filters:
	 * - fromDate/toDate: Date range in YYYY-MM-DD format
	 * - eventType: Filter by event type (CHECK_UP, FOLLOW_UP)
	 * - includeCancelled: Include cancelled events (default: false)
	 * - future: When 'true', return only events at/after now. Omitted (the
	 *   default) returns every event in the date range, past and future.
	 *
	 * Pagination:
	 * - limit: Max results (1-10000, default: 50)
	 * - startingAfter/endingBefore: opaque event list cursors
	 */
	list: oc
		.route({
			method: "GET",
			path: "/api/events",
			summary: "List events with filtering and pagination",
			tags: ["Events"],
		})
		.input(
			z.object({
				fromDate: z
					.string()
					.regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD")
					.optional(),
				toDate: z
					.string()
					.regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD")
					.optional(),
				eventType: z.string().max(100).optional(),
				includeCancelled: z
					.string()
					.regex(/^(true|false)$/)
					.optional(),
				future: z
					.string()
					.regex(/^(true|false)$/)
					.optional(),
				limit: z
					.string()
					.regex(/^\d+$/)
					.refine((v) => {
						const n = Number(v);
						return n >= 1 && n <= 10_000;
					}, "Limit must be between 1 and 10000")
					.optional(),
				startingAfter: z.string().max(255).optional(),
				endingBefore: z.string().max(255).optional(),
			})
		)
		.output(ListEventsResponseSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			DATABASE_ERROR: {
				message: "Failed to list events",
			},
		}),

	/**
	 * Get event by numeric ID.
	 */
	getById: oc
		.route({
			method: "GET",
			path: "/api/events/id/{id}",
			summary: "Get event by ID",
			tags: ["Events"],
		})
		.input(
			z.object({
				id: z.coerce.number().int().min(1),
			})
		)
		.output(EventSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			NOT_FOUND: {
				message: "Event not found",
				data: z.object({ id: z.number() }),
			},
			INVALID_ID: {
				message: "Invalid event ID",
			},
			DATABASE_ERROR: {
				message: "Failed to retrieve event",
			},
		}),

	/**
	 * Get events for a user by phone number.
	 */
	getByPhone: oc
		.route({
			method: "GET",
			path: "/api/events/{phoneE164}",
			summary: "Get events by phone number",
			tags: ["Events"],
		})
		.input(
			z.object({
				phoneE164: PhoneE164Schema,
				includePast: z
					.string()
					.regex(/^(true|false)$/)
					.optional(),
			})
		)
		.output(ListEventsResponseSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			NOT_FOUND: {
				message: "User not found",
				data: z.object({ phoneE164: z.string() }),
			},
			USER_LOOKUP_FAILED: {
				message: "Failed to get or create user",
			},
			DATABASE_ERROR: {
				message: "Failed to retrieve events",
			},
		}),

	/**
	 * Create a new event.
	 */
	create: oc
		.route({
			method: "POST",
			path: "/api/events",
			summary: "Create a new event",
			tags: ["Events"],
		})
		.input(
			z
				.object({
					phoneE164: StrictPhoneE164InputSchema.optional(),
					contactId: z.string().trim().min(1).optional(),
					userName: z.string().trim().min(1).max(200).optional(),
					eventDateTime: z.string().datetime(),
					type: EventTypeSchema,
					hijri: z.boolean().optional(),
					language: z.enum(["ar", "en"]).optional(),
					source: DashboardEventSourceSchema.optional(),
				})
				.refine(
					(value) =>
						Boolean(value.phoneE164 || value.userName || value.contactId),
					{ message: "A name, phone number or existing contact is required" }
				)
		)
		.output(CreateEventResponseSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			VALIDATION_ERROR: {
				message: "Invalid event data",
				data: z.object({
					errors: z.array(
						z.object({
							path: z.string(),
							message: z.string(),
						})
					),
				}),
			},
			SLOT_UNAVAILABLE: {
				message: "Time slot unavailable",
			},
			DATABASE_ERROR: {
				message: "Failed to create event",
			},
		}),

	/**
	 * Modify an existing event.
	 */
	modify: oc
		.route({
			method: "PATCH",
			path: "/api/events/{id}",
			summary: "Modify an existing event",
			tags: ["Events"],
		})
		.input(
			z.object({
				id: z.coerce.number().int().min(1),
				phoneE164: StrictPhoneE164InputSchema.optional(),
				contactId: z.string().trim().min(1).optional(),
				newEventDateTime: z.string().datetime().optional(),
				newType: EventTypeSchema.optional(),
				newName: z.string().trim().max(200).optional(),
				approximate: z.boolean().optional(),
				previousPhoneE164: PhoneE164Schema.optional(),
			})
		)
		.output(ModifyEventResponseSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			NOT_FOUND: {
				message: "Event not found",
				data: z.object({ id: z.number() }),
			},
			VALIDATION_ERROR: {
				message: "Invalid event request",
				data: z.object({
					errors: z.array(
						z.object({
							path: z.string(),
							message: z.string(),
						})
					),
				}),
			},
			DATABASE_ERROR: {
				message: "Failed to modify event",
			},
		}),

	/**
	 * Get the notes attached to an event.
	 */
	getNotes: oc
		.route({
			method: "GET",
			path: "/api/events/{id}/notes",
			summary: "Get event notes",
			tags: ["Events"],
		})
		.input(
			z.object({
				id: z.coerce.number().int().min(1),
			})
		)
		.output(EventNotesResponseSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			NOT_FOUND: {
				message: "Event not found",
				data: z.object({ id: z.number() }),
			},
			INVALID_ID: {
				message: "Invalid event ID",
			},
			DATABASE_ERROR: {
				message: "Failed to retrieve event notes",
			},
		}),

	/**
	 * Update the notes attached to an event.
	 */
	updateNotes: oc
		.route({
			method: "PATCH",
			path: "/api/events/{id}/notes",
			summary: "Update event notes",
			tags: ["Events"],
		})
		.input(
			UpdateEventNotesInputSchema.extend({
				id: z.coerce.number().int().min(1),
			})
		)
		.output(EventNotesResponseSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			NOT_FOUND: {
				message: "Event not found",
				data: z.object({ id: z.number() }),
			},
			INVALID_ID: {
				message: "Invalid event ID",
			},
			DATABASE_ERROR: {
				message: "Failed to update event notes",
			},
		}),

	/**
	 * Cancel an event by ID.
	 */
	cancel: oc
		.route({
			method: "POST",
			path: "/api/events/{id}/cancel",
			summary: "Cancel event by ID",
			tags: ["Events"],
		})
		.input(
			z.object({
				id: z.coerce.number().int().min(1),
			})
		)
		.output(CancelEventResponseSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			NOT_FOUND: {
				message: "Event not found",
				data: z.object({ id: z.number() }),
			},
			DATABASE_ERROR: {
				message: "Failed to cancel event",
			},
		}),

	/**
	 * Reinstate a previously cancelled event.
	 */
	reinstate: oc
		.route({
			method: "POST",
			path: "/api/events/{id}/reinstate",
			summary: "Reinstate a cancelled event",
			tags: ["Events"],
		})
		.input(
			z.object({
				id: z.coerce.number().int().min(1),
			})
		)
		.output(
			z.object({
				reinstated: z.boolean(),
			})
		)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			NOT_FOUND: {
				message: "Event not found",
				data: z.object({ id: z.number() }),
			},
			DATABASE_ERROR: {
				message: "Failed to reinstate event",
			},
		}),

	/**
	 * Mark an event as completed.
	 */
	complete: oc
		.route({
			method: "POST",
			path: "/api/events/{id}/complete",
			summary: "Mark event completed",
			tags: ["Events"],
		})
		.input(
			z.object({
				id: z.coerce.number().int().min(1),
			})
		)
		.output(CompleteEventResponseSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			NOT_FOUND: {
				message: "Event not found",
				data: z.object({ id: z.number() }),
			},
			DATABASE_ERROR: {
				message: "Failed to complete event",
			},
		}),

	/**
	 * Mark an event as no-show.
	 */
	noShow: oc
		.route({
			method: "POST",
			path: "/api/events/{id}/no-show",
			summary: "Mark event no-show",
			tags: ["Events"],
		})
		.input(
			z.object({
				id: z.coerce.number().int().min(1),
			})
		)
		.output(MarkNoShowResponseSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			NOT_FOUND: {
				message: "Event not found",
				data: z.object({ id: z.number() }),
			},
			DATABASE_ERROR: {
				message: "Failed to mark event no-show",
			},
		}),

	/**
	 * Audit timeline for an event (who did what, when).
	 */
	getHistory: oc
		.route({
			method: "GET",
			path: "/api/events/{id}/history",
			summary: "Get event audit timeline",
			tags: ["Events"],
		})
		.input(
			z.object({
				id: z.coerce.number().int().min(1),
			})
		)
		.output(EventHistoryResponseSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			NOT_FOUND: {
				message: "Event not found",
				data: z.object({ id: z.number() }),
			},
			INVALID_ID: {
				message: "Invalid event ID",
			},
		}),

	/**
	 * Cancel event(s) for a user by phone number.
	 * Optionally filter by event datetime.
	 */
	cancelByPhone: oc
		.route({
			method: "DELETE",
			path: "/api/events/user/{phoneE164}",
			summary: "Cancel user events by phone number",
			tags: ["Events"],
		})
		.input(
			z.object({
				phoneE164: PhoneE164Schema,
				date: z.string().datetime().optional(),
			})
		)
		.output(CancelByPhoneResponseSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			NOT_FOUND: {
				message: "User not found",
				data: z.object({ phoneE164: z.string() }),
			},
			USER_LOOKUP_FAILED: {
				message: "Failed to get or create user",
			},
			DATABASE_ERROR: {
				message: "Failed to cancel event",
			},
		}),

	/**
	 * Find the next upcoming event for a user by phone number.
	 */
	findUpcoming: oc
		.route({
			method: "GET",
			path: "/api/events/upcoming/{phoneE164}",
			summary: "Find upcoming event for user",
			tags: ["Events"],
		})
		.input(
			z.object({
				phoneE164: PhoneE164Schema,
			})
		)
		.output(FindUpcomingEventResponseSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			DATABASE_ERROR: {
				message: "Failed to find event",
			},
		}),

	/**
	 * Get available time slots for multiple dates.
	 * Used by the AI assistant to suggest appointment times.
	 */
	getAvailableSlots: oc
		.route({
			method: "POST",
			path: "/api/events/available-slots",
			summary: "Get available time slots for dates",
			tags: ["Events"],
		})
		.input(
			z.object({
				dates: z
					.array(
						z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD")
					)
					.min(1)
					.max(90),
				hijri: z.boolean().optional(),
			})
		)
		.output(GetAvailableSlotsBatchResponseSchema)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			VALIDATION_ERROR: {
				message: "Invalid date format",
				data: z.object({
					errors: z.array(
						z.object({
							path: z.string(),
							message: z.string(),
						})
					),
				}),
			},
			DATABASE_ERROR: {
				message: "Failed to get available slots",
			},
		}),

	/**
	 * Count events by type.
	 * Used to show warning counts when deleting event types.
	 */
	countByType: oc
		.route({
			method: "POST",
			path: "/api/events/count-by-type",
			summary: "Count events grouped by type",
			tags: ["Events"],
		})
		.input(
			z.object({
				types: z.array(z.string().min(1).max(100)).max(50).optional(),
			})
		)
		.output(
			z.object({
				counts: z.record(z.string(), z.number()),
			})
		)
		.errors({
			UNAUTHORIZED: commonErrors.UNAUTHORIZED,
			DATABASE_ERROR: {
				message: "Failed to count events",
			},
		}),
};

// =============================================================================
// Type Exports
// =============================================================================

import type {
	InferContractRouterInputs,
	InferContractRouterOutputs,
} from "@orpc/contract";

export type EventsContract = typeof eventsContract;

// Schema-derived types
export type EventType = z.infer<typeof EventTypeSchema>;
export type EventStatus = z.infer<typeof EventStatusSchema>;
export type NotificationSource = z.infer<typeof NotificationSourceSchema>;
export type EventListItem = z.infer<typeof EventListItemSchema>;
export type Event = z.infer<typeof EventSchema>;
export type CreateEventResponse = z.infer<typeof CreateEventResponseSchema>;
export type ModifyEventResponse = z.infer<typeof ModifyEventResponseSchema>;
export type CancelEventResponse = z.infer<typeof CancelEventResponseSchema>;
export type CompleteEventResponse = z.infer<typeof CompleteEventResponseSchema>;
export type MarkNoShowResponse = z.infer<typeof MarkNoShowResponseSchema>;
export type CancelByPhoneResponse = z.infer<typeof CancelByPhoneResponseSchema>;
export type EventNoteFields = z.infer<typeof EventNoteFieldsSchema>;
export type EventNoteCanvas = z.infer<typeof EventNoteCanvasSchema>;
export type EventNotesResponse = z.infer<typeof EventNotesResponseSchema>;
export type UpdateEventNotesInput = z.infer<typeof UpdateEventNotesInputSchema>;
export type EventHistoryAction = z.infer<typeof EventHistoryActionSchema>;
export type EventHistoryChange = z.infer<typeof EventHistoryChangeSchema>;
export type EventHistoryActor = z.infer<typeof EventHistoryActorSchema>;
export type EventHistoryEntry = z.infer<typeof EventHistoryEntrySchema>;
export type EventHistoryResponse = z.infer<typeof EventHistoryResponseSchema>;
export type ListEventsResponse = z.infer<typeof ListEventsResponseSchema>;
export type FindUpcomingEventResponse = z.infer<
	typeof FindUpcomingEventResponseSchema
>;
export type AvailableTimeSlotsDate = z.infer<
	typeof AvailableTimeSlotsDateSchema
>;
export type GetAvailableSlotsBatchResponse = z.infer<
	typeof GetAvailableSlotsBatchResponseSchema
>;

// Contract input/output types using oRPC inference utilities
type EventsInputs = InferContractRouterInputs<EventsContract>;
type EventsOutputs = InferContractRouterOutputs<EventsContract>;

// Input types
export type ListEventsInput = EventsInputs["list"];
export type GetEventByIdInput = EventsInputs["getById"];
export type GetEventsByPhoneInput = EventsInputs["getByPhone"];
export type CreateEventInput = EventsInputs["create"];
export type ModifyEventInput = EventsInputs["modify"];
export type CancelEventInput = EventsInputs["cancel"];
export type ReinstateEventInput = EventsInputs["reinstate"];
export type CompleteEventInput = EventsInputs["complete"];
export type NoShowEventInput = EventsInputs["noShow"];
export type CancelByPhoneInput = EventsInputs["cancelByPhone"];
export type FindUpcomingInput = EventsInputs["findUpcoming"];
export type GetAvailableSlotsInput = EventsInputs["getAvailableSlots"];
export type CountByTypeInput = EventsInputs["countByType"];

// Output types (for completeness)
export type ListEventsOutput = EventsOutputs["list"];
export type GetEventByIdOutput = EventsOutputs["getById"];
export type GetEventsByPhoneOutput = EventsOutputs["getByPhone"];
export type CreateEventOutput = EventsOutputs["create"];
export type ModifyEventOutput = EventsOutputs["modify"];
export type CancelEventOutput = EventsOutputs["cancel"];
export type ReinstateEventOutput = EventsOutputs["reinstate"];
export type CompleteEventOutput = EventsOutputs["complete"];
export type NoShowEventOutput = EventsOutputs["noShow"];
export type CancelByPhoneOutput = EventsOutputs["cancelByPhone"];
export type FindUpcomingOutput = EventsOutputs["findUpcoming"];
export type GetAvailableSlotsOutput = EventsOutputs["getAvailableSlots"];
export type CountByTypeOutput = EventsOutputs["countByType"];
